import { defCalRoute } from './common';
import { SignupWizard } from './components/signup-wizard';
import { DomainStep } from './components/domain-step';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { useEffect, useState } from 'preact/hooks';
import { ComponentType } from 'preact';
import { rpx } from 'client/lib/rpx-client';
import { useAuth } from 'client/lib/auth';
import type { PaneProps, Step, CalendarPurchase } from './types';
import { InfoStep } from './components/signup-wizard/info-step';
import { AuthStep } from './components/signup-wizard/auth-step';
import { PaymentStep } from './components/signup-wizard/payment-step';
import { ProvisioningStep } from './components/signup-wizard/provisioning-step';
import { CalendarStep } from './components/signup-wizard/calendar-step';

export const route = defCalRoute({
  isPublic: true,
  load,
  Page,
});

async function load(props: RouteLoadProps) {
  let initialStep: Step = 'auth';
  if (props.params.code) {
    await props.auth.login(rpx.auth.oneClickAutoLogin({ resetCode: props.params.code }));
    initialStep = 'payment';
    history.replaceState(null, '', location.pathname);
  }
  const pricing = await rpx.pricingTable.getCalendarPricing({
    priceId: props.params.priceId,
    couponId: props.params.couponId,
  });

  if (!props.auth.user) {
    return {
      ...pricing,
      hasDefaultPrice: false,
      calendarPurchase: undefined,
      integrations: undefined,
      eventType: undefined,
      initialStep,
    };
  }

  const [corePurchases, integrations, eventTypes] = await Promise.all([
    rpx.myCorePayments.getCorePurchases().catch(() => undefined),
    rpx.ruzcal.getCalIntegrations({
      isForSignup: true,
    }),
    rpx.ruzcalEventTypes.getEventTypesList(),
  ]);

  return {
    ...pricing,
    initialStep: initialStep as Step,
    calendarPurchase: corePurchases?.calendarPurchase,
    integrations,
    eventType: eventTypes[0],
  };
}

const stepSequence: Step[] = ['info', 'auth', 'payment', 'provision', 'calendar', 'domain'];

function Page(loadResult: LoadedProps<typeof load>) {
  const auth = useAuth();
  const calendarPurchase = loadResult.state.calendarPurchase;

  const [wizardState, setWizardState] = useState<{
    currentStep: Step;
    prevStepIndex: number;
    calendarPurchase: CalendarPurchase | undefined;
  }>(() => {
    // Determine initial step based on user state and purchase status
    let initialStep = loadResult.state.initialStep;

    // Check if user has an active or provisioning calendar purchase
    const hasCalendarPurchase =
      calendarPurchase?.purchase?.status === 'active' ||
      calendarPurchase?.purchase?.status === 'provisioning';

    if (auth.user?.calendarTier === 'core' || hasCalendarPurchase) {
      initialStep = 'provision';
    }

    return {
      currentStep: initialStep,
      prevStepIndex: 0,
      calendarPurchase,
    };
  });

  const currentStepIndex = stepSequence.indexOf(wizardState.currentStep);

  // Track direction for animations
  const fadeDirection =
    currentStepIndex >= wizardState.prevStepIndex ? 'an-fade-in-left' : 'an-fade-in-right';

  // Auto-advance logged-in users past auth step (but not temporary users)
  useEffect(() => {
    if (auth.user && wizardState.currentStep === 'auth') {
      goToNextStep();
    }
  }, [auth.user, wizardState.currentStep]);

  const updateCalendarPurchase = (purchase: CalendarPurchase | undefined) => {
    setWizardState((prev) => ({
      ...prev,
      calendarPurchase: purchase,
    }));
  };

  const goToNextStep = () => {
    const i = stepSequence.indexOf(wizardState.currentStep);
    const next = Math.min(i + 1, stepSequence.length - 1);
    let step = stepSequence[next];

    // Skip auth step if already logged in
    if (step === 'auth' && auth.user) {
      step = 'payment';
    }

    // Skip payment step if already has subscription or purchase
    const hasCalendarPurchase =
      wizardState.calendarPurchase?.purchase?.status === 'active' ||
      wizardState.calendarPurchase?.purchase?.status === 'provisioning';
    if (step === 'payment' && (auth.user?.calendarTier === 'core' || hasCalendarPurchase)) {
      step = 'provision';
    }

    const { integrations } = loadResult.state;
    // Skip calendar step if both integrations are available
    if (step === 'calendar' && integrations?.hasZoom && integrations?.hasGoogle) {
      step = 'domain';
    }

    setWizardState((prev) => ({
      ...prev,
      currentStep: step,
      prevStepIndex: currentStepIndex,
    }));
  };

  const panes: Record<Step, ComponentType<PaneProps>> = {
    info: InfoStep,
    auth: AuthStep,
    payment: PaymentStep,
    provision: ProvisioningStep,
    calendar: CalendarStep,
    domain: DomainStep as ComponentType<PaneProps>, // DomainStep doesn't use props
  };

  const Pane = panes[wizardState.currentStep];
  const isFullPage = wizardState.currentStep === 'info';

  return (
    <SignupWizard
      currentStep={currentStepIndex}
      totalSteps={6}
      fadeDirection={fadeDirection}
      isFullPage={isFullPage}
    >
      {wizardState.currentStep === 'domain' ? (
        <DomainStep />
      ) : (
        <Pane
          isFullPage={isFullPage}
          onNext={goToNextStep}
          price={loadResult.state.price}
          coupon={loadResult.state.coupon}
          calendarPurchase={wizardState.calendarPurchase}
          updateCalendarPurchase={updateCalendarPurchase}
          integrations={loadResult.state.integrations}
          eventType={loadResult.state.eventType}
        />
      )}
    </SignupWizard>
  );
}
